import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBreadcrumbStore } from '@/stores/breadcrumb'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
      {
        path: 'pakkeliste',
        name: 'packing',
        component: () => import('@/views/PackingView.vue'),
      },
      { path: 'ordrer', name: 'orders', component: () => import('@/views/OrdersView.vue') },
      {
        path: 'ordrer/:id',
        name: 'order-detail',
        component: () => import('@/views/OrderDetailView.vue'),
      },
      {
        path: 'indkob',
        name: 'purchasing',
        component: () => import('@/views/PurchasingView.vue'),
      },
      {
        path: 'indkob/ordrer',
        name: 'purchase-orders',
        component: () => import('@/views/PurchaseOrdersView.vue'),
      },
      {
        path: 'indkob/ordrer/:id',
        name: 'purchase-order-detail',
        component: () => import('@/views/PurchaseOrderDetailView.vue'),
      },
      {
        path: 'abonnementer',
        name: 'subscriptions',
        component: () => import('@/views/SubscriptionsView.vue'),
      },
      {
        // Before the :id route, or "planer" would be read as a subscription id and 404.
        path: 'abonnementer/planer',
        name: 'subscription-plans',
        component: () => import('@/views/SubscriptionPlansView.vue'),
      },
      {
        path: 'abonnementer/:id',
        name: 'subscription-detail',
        component: () => import('@/views/SubscriptionDetailView.vue'),
      },
      { path: 'rapporter', name: 'reports', component: () => import('@/views/ReportsView.vue') },
      {
        // Super-admin only, like the migration below it: the connections hold the shop's own
        // credentials, and a shop administrator reads the sync history through the dashboard.
        path: 'kanaler',
        name: 'channels',
        component: () => import('@/views/ChannelsView.vue'),
        meta: { requiresPlatformAdmin: true },
      },
      {
        path: 'kanaler/dataflytning',
        name: 'migration',
        component: () => import('@/views/MigrationView.vue'),
        meta: { requiresPlatformAdmin: true },
      },
      {
        path: 'mailforslag',
        name: 'campaigns',
        component: () => import('@/views/CampaignsView.vue'),
        meta: { requiresAdmin: true },
      },
      { path: 'beskeder', name: 'messages', component: () => import('@/views/MessagesView.vue') },
      {
        path: 'beskeder/skabeloner',
        name: 'message-templates',
        component: () => import('@/views/MessageTemplatesView.vue'),
      },
      { path: 'returer', name: 'returns', component: () => import('@/views/ReturnsView.vue') },
      { path: 'kunder', name: 'customers', component: () => import('@/views/CustomersView.vue') },
      {
        path: 'produkter/kategorier',
        name: 'categories',
        component: () => import('@/views/CategoriesView.vue'),
      },
      {
        path: 'produkter/varianttyper',
        name: 'variant-types',
        component: () => import('@/views/VariantTypesView.vue'),
      },
      {
        path: 'produkter/maerker',
        name: 'brands',
        component: () => import('@/views/BrandsView.vue'),
      },
      { path: 'produkter', name: 'products', component: () => import('@/views/ProductsView.vue') },
      {
        path: 'fragtregler',
        name: 'shipping-rules',
        component: () => import('@/views/ShippingRulesView.vue'),
      },
      {
        path: 'fragtaftaler',
        name: 'carriers',
        component: () => import('@/views/CarriersView.vue'),
        meta: { requiresPlatformAdmin: true },
      },
      {
        path: 'produkter/lagerjournal',
        name: 'stock-movements',
        component: () => import('@/views/StockMovementsView.vue'),
      },
      {
        path: 'leverandorkatalog',
        name: 'catalogue',
        component: () => import('@/views/CatalogueView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'leverandorer',
        name: 'suppliers',
        component: () => import('@/views/SuppliersView.vue'),
      },
      {
        path: 'leverandorer/:id',
        name: 'supplier-detail',
        component: () => import('@/views/SupplierDetailView.vue'),
      },
      {
        path: 'members',
        name: 'members',
        component: () => import('@/views/MembersView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        // Above every workspace rather than inside one, but it lives in the shell because the
        // person using it is signed in to their own workspace like anybody else.
        path: 'platform',
        name: 'platform',
        component: () => import('@/views/PlatformView.vue'),
        meta: { requiresPlatformAdmin: true },
      },
      {
        path: 'platform/opkoblinger',
        name: 'integrations',
        component: () => import('@/views/IntegrationsView.vue'),
        meta: { requiresPlatformAdmin: true },
      },
      { path: 'settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
      {
        path: 'settings/egne-ord',
        name: 'translations',
        component: () => import('@/views/TranslationsView.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },
  {
    // Outside the shell on purpose: a packing slip is printed, and the sidebar and topbar have no
    // business on the sheet — nor on the screen while somebody checks it before hitting print.
    path: '/pakkesedler',
    name: 'packing-slips',
    component: () => import('@/views/PackingSlipsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Public, and outside the shell. Whoever opens this is a customer holding a link from an
    // email, not staff — the token in the path is the whole authentication.
    path: '/mit-abonnement/:token',
    name: 'my-subscription',
    component: () => import('@/views/MySubscriptionView.vue'),
    meta: { public: true },
  },
  {
    // Before /tilmeld/:key, or "bekraeft" would be read as a plan key and 404.
    path: '/tilmeld/bekraeft/:token',
    name: 'confirm-sign-up',
    component: () => import('@/views/ConfirmSignUpView.vue'),
    meta: { public: true },
  },
  {
    // Also public, and the address the shop puts on its own website. The key in the path names the
    // plan and, through it, the workspace — nothing else identifies either.
    path: '/tilmeld/:key',
    name: 'plan-sign-up',
    component: () => import('@/views/SignUpPlanView.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/glemt-adgangskode',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    // The single path segment is the whole link: workspace, account and token packed together by
    // the server. Nothing here decodes it — the screen hands it straight back.
    path: '/nulstil-adgangskode/:token',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/bekraeft-email/:token',
    name: 'confirm-email',
    component: () => import('@/views/ConfirmEmailView.vue'),
    meta: { public: true },
  },
  {
    // Its own page rather than the reset one: the reader has not forgotten anything, they have
    // been given access to a shop, and the page has to be able to say so.
    path: '/invitation/:token',
    name: 'accept-invitation',
    component: () => import('@/views/AcceptInvitationView.vue'),
    meta: { public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Cleared before the next screen renders. A detail screen sets it after loading; showing the
  // previous order's number beside this one's content would be worse than showing none.
  useBreadcrumbStore().clear()

  // One session probe on first navigation; afterwards the store answers from memory.
  await auth.restore()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'dashboard' }
  }

  if (to.meta.requiresPlatformAdmin && !auth.isPlatformAdmin) {
    return { name: 'dashboard' }
  }

  if (to.meta.public && auth.isAuthenticated && to.name === 'login') {
    return { name: 'dashboard' }
  }

  return true
})
